import { type Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';

import { StripeLogger } from './logger';

/**
 * Loads the Stripe SDK with retry logic, as per the Stripe team's recommendation.
 * > _If you're using stripe-js v2.3.0 or newer you can wrap loading Stripe JS in try-catch logic and retry loading from there._
 */
export async function loadStripeWithRetry(
  publishableKey: string,
  options: LoadStripeOptions = {},
): Promise<Stripe | null> {
  const { timeoutMs = 5000 } = options;

  return await withTimeout(retryStripeLoad(publishableKey, options), timeoutMs);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function retryStripeLoad(publishableKey: string, options: LoadStripeOptions) {
  const { maxAttempts = 3, retryDelayMs = 1000 } = options;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await loadStripe(publishableKey, STRIPE_LOAD_OPTIONS);
    } catch (error) {
      StripeLogger.logStripeLoadError(error as Error);

      if (attempt < maxAttempts) await sleep(retryDelayMs);
    }
  }

  return Promise.reject('Something went wrong while loading the payment provider.');
}

/**
 * If the promise does not resolve within the timeout, reject with the given message.
 * This can happen if `loadStripe` neither resolves nor rejects, leading to a stuck state.
 */
async function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<T>((_resolve, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error('Timeout loading Stripe'));
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, timeout]);
  } catch (error) {
    StripeLogger.logStripeLoadError(error as Error);

    return Promise.reject(error);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// ─── Types ───────────────────────────────────────────────────────────────────

type LoadStripeOptions = {
  maxAttempts?: number;
  retryDelayMs?: number;
  timeoutMs?: number;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const STRIPE_LOAD_OPTIONS = {
  developerTools: {
    assistant: {
      enabled: false,
    },
  },
};
